import { observer } from 'mobx-react';
import { useForm } from 'react-hook-form';
import yup from '../../core/utils/yupValidate';
import { yupResolver } from '@hookform/resolvers/yup';
import { MESSAGE_SEND_METHOD, MSG } from '../../core/configs/constants';
import { useEffect } from 'react';
import { ReactNotifications } from '../../components';
import { useStore } from '../../core/utils/hook';
import { flowResult } from 'mobx';

const SendMessageScreen = observer(({selectedStudent, selectedRowIds, setSelectedStudent}) => {

    // store
    const { messageStore: { sendMessage } } = useStore();

    // state
    const validateSchema = yup.object().shape({
        title: yup.string().required(MSG['error.required']),
        content: yup.string().required(MSG['error.required']),
        sendTo: yup.array().min(1, 'メッセージを送信する受講者を選択してください。'),
        sendType: yup.number(),
        sendMethod: yup.array()
            .typeError('配送方法を選択してください。')
            .min(1, '配送方法を少なくとも1つ選択してください。')
    })

    const defaultValues = { sendType: 1 };

    const { register, handleSubmit, 
        formState: { errors, isSubmitting }, setValue, clearErrors, reset 
    } = useForm({resolver: yupResolver(validateSchema), mode: 'onChange', defaultValues});

    // lifecycle
    useEffect(() => {
        window.$('#sendMessageScreenModal').modal({
            backdrop: 'static',
            keyboard: false
        });
    }, [])

    useEffect(() => {
        setValue('sendTo', Object.keys(selectedRowIds).map(e => Number(e)), { shouldValidate: true });
    }, [selectedRowIds])

    // function
    const onCloseModal = () => {
        window.$('#sendMessageScreenModal').modal('hide');
        reset(formValues => ({
            ...formValues,
            title: '',
            content: '',
            sendTo: [],
            sendType: 1,
            sendMethod: []
        }))
        clearErrors();
    }

    const clearSelectedStudent = () => {
        setSelectedStudent([]);
        onCloseModal();
    }

    const onDeleteSelectedStudent = (id) => {
        const index = selectedStudent.findIndex(e => e.id === id);
        if(index > -1) {
            setSelectedStudent(state => ([
                ...state.slice(0, index),
                ...state.slice(index + 1)
            ]))
        }
    }

    const onSendMessage = async (data) => {
        
        const formattedData = {
            ...data,
            sendMethod: data.sendMethod.map(Number) 
        };

        // console.log(formattedData.sendMethod); 

        const res = await flowResult(sendMessage(formattedData));
        if(res) {
            setSelectedStudent([]);
            onCloseModal();
            ReactNotifications('success', '正常にメッセージを送信しました。');
        }
    }

    const onError = async (error) => {
        console.log(error)
    }

    return(
        <div className='modal' id='sendMessageScreenModal'>
            <div className='modal-dialog'>
                <div className='modal-content'>
                    {/* Modal Header */}
                    <div className='modal-header'>
                        <h3 className='title-1'>メッセージ送信</h3>
                        <button type='button' className='btn-close' onClick={onCloseModal}></button>
                    </div>
                    {/* Modal body */}
                    <div className='modal-body'>
                        <form onSubmit={handleSubmit(onSendMessage, onError)}>
                            <div className='content-pupop'>
                                {/* SELECTED STUDENT */}
                                <div className='content-left'>
                                    <div style={{ display: 'flex', borderBottom: '1px dashed rgba(0, 0, 0, 0.1)',
                                        alignItems: 'center', justifyContent: 'space-between', marginBottom: '15px' }}>
                                        <h4 className='titl-3'>合計: {selectedStudent?.length ?? 0}</h4>
                                        <p 
                                            role={'button'}  
                                            onClick={clearSelectedStudent}
                                            style={{color: 'rgba(255, 0, 0, 1)'}}
                                        >
                                        すべてのデータを削除する
                                        </p>
                                    </div>
                                    {
                                        selectedStudent?.length > 0 &&
                                    <ul>
                                        {
                                            selectedStudent.map(e => (
                                                <li key={e.id}>
                                                    <a>
                                                        <div style={{ display: 'flex', flexDirection: 'column', 
                                                            alignItems: 'start', justifyContent: 'flex-start' }}>
                                                            {`${e?.firstName || ''} ${e?.lastName || ''}`}
                                                            <span style={{ color: 'rgba(119, 119, 119, 1)', fontSize: '14px', fontWeight: 'normal' }}>
                                                                ({e?.email || ''})
                                                            </span>
                                                        </div>
                                                      
                                                        <button 
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer' }} 
                                                            onClick={() => onDeleteSelectedStudent(e.id)}
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" 
                                                                width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F60004" 
                                                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                                                                className="lucide lucide-circle-x-icon lucide-circle-x"
                                                            >
                                                                <circle cx="12" cy="12" r="10"/>
                                                                <path d="m15 9-6 6"/><path d="m9 9 6 6"/>
                                                            </svg>
                                                        </button>
                                                    </a>
                                                </li>
                                            ))
                                        }
                                    </ul>
                                    }
                                  
                                </div>
                                {/* MESSAGE CONTENT */}
                                <div className='content-right'>
                                
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '12px' }}>
                                        
                                        <label htmlFor='sendMethod'>ファイルの種類 <span className='text-danger'>*</span></label>
                                        <div className="flex flex-gap-20 wrap start" 
                                            style={{ width: '100%', border: '1px dashed #ced4da', padding: '9px 10px', borderRadius: '5px' }}
                                        >
                                            <div className="">
                                                <input
                                                    type="checkbox"
                                                    {...register('sendMethod')}
                                                    style={{
                                                        transform: 'scale(1.35) translateX(3px)'
                                                    }}
                                                    className="mg-r-15"
                                                    value={`${MESSAGE_SEND_METHOD.NORMAL.value}`}   
                                                />
                                                <span>メッセージで送信する</span>
                                            </div>
                                            <div className="">
                                                <input
                                                    type="checkbox"
                                                    {...register('sendMethod')}
                                                    style={{
                                                        transform: 'scale(1.35) translateX(3px)'
                                                    }}
                                                    className="mg-r-15"
                                                    value={`${MESSAGE_SEND_METHOD.MAIL.value}`}   
                                                />
                                                <span>受講者のメールアドレスへ送信する</span>
                                            </div>
                                           
                                        </div>
                                    </div>
                                    {
                                        errors?.sendMethod &&
                                        <div className='text-danger mg-t-5'>{errors.sendMethod?.message}</div>
                                    }
                                
                                    <label htmlFor='title'>タイトル <span className='text-danger'>*</span></label>
                                    <input type='text' id='title' {...register('title')} maxLength={255}/>
                                    {
                                        errors?.title &&
                                        <div className='text-danger mg-t-5'>{errors.title?.message}</div>
                                    }
                                    <label htmlFor='content'>本文 <span className='text-danger'>*</span></label>
                                    <textarea {...register('content')} id='content' cols={30} rows={10}/>
                                    {
                                        errors?.content &&
                                        <div className='text-danger mg-t-5'>{errors.content?.message}</div>
                                    }
                                    {
                                        errors?.sendTo &&
                                        <div className='text-danger mg-t-5'>{errors.sendTo?.message}</div>
                                    }
                                    
                                
                                </div>
                            </div>
                       
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                gap: '50px',
                                borderTop: '1px solid #d3d3d3',
                                padding: '20px' 
                            }}>
                                <button 
                                    className='btn-cancel'
                                    type='button' 
                                    onClick={onCloseModal}
                                >
                                            キャンセル
                                </button>
                                <button 
                                    className='btn-submit'
                                    type='submit'

                                >送信</button> 
                            </div>
                        </form>
                       
                    </div>
                </div>
            </div>
        </div>
    )
})

export default SendMessageScreen;